import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api, { userAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
    Search, Users, Trash2, Loader2, 
    MoreVertical, Shield, ShieldCheck, ShieldAlert, Plus, UserPlus, Key, Copy, Check, Eye, EyeOff, Pencil
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const RoleBadge = ({ role }) => {
    const styles = {
        admin: { bg: 'bg-red-500/15', text: 'text-red-400', icon: ShieldAlert },
        fleet_manager: { bg: 'bg-[#1B7EB5]/15', text: 'text-[#58A6B4]', icon: ShieldCheck },
        driver: { bg: 'bg-green-500/15', text: 'text-green-400', icon: Shield }
    };
    const style = styles[role] || styles.driver;
    const Icon = style.icon;
    const label = role === 'fleet_manager' ? 'Fleet Manager' : role.charAt(0).toUpperCase() + role.slice(1);
    
    return (
        <span className={`badge ${style.bg} ${style.text} inline-flex items-center gap-1`}>
            <Icon className="w-3 h-3" />
            {label}
        </span>
    );
};

export default function UsersPage() {
    const { user: currentUser, companyEdition } = useAuth();
    const { language } = useLanguage();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Add user modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [addingUser, setAddingUser] = useState(false);
    const [newUser, setNewUser] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        role: 'driver'
    });
    
    // Reset password modal state
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetUser, setResetUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [resettingPassword, setResettingPassword] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [copied, setCopied] = useState(false);
    
    // Edit user modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editUserData, setEditUserData] = useState({ name: '', phone: '', role: '' });
    const [savingUser, setSavingUser] = useState(false);
    
    // Check if Personal Edition (max 3 users)
    const isPersonalEdition = companyEdition === 'personal';
    const maxUsers = isPersonalEdition ? 3 : null;
    const canAddMore = !maxUsers || users.length < maxUsers;

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await userAPI.getAll();
            setUsers(response.data);
        } catch (error) {
            toast.error('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async () => {
        if (!newUser.name || !newUser.email || !newUser.password) {
            toast.error(language === 'el' ? 'Συμπληρώστε όλα τα υποχρεωτικά πεδία' : 'Fill in all required fields');
            return;
        }
        
        // Phone is required for fleet_manager
        if (newUser.role === 'fleet_manager' && !newUser.phone) {
            toast.error(language === 'el' ? 'Το τηλέφωνο είναι υποχρεωτικό για τον Υπεύθυνο Στόλου' : 'Phone is required for Fleet Manager');
            return;
        }
        
        setAddingUser(true);
        try {
            await api.post('/users', newUser);
            toast.success(language === 'el' ? 'Ο χρήστης δημιουργήθηκε!' : 'User created!');
            setShowAddModal(false);
            setNewUser({ name: '', email: '', phone: '', password: '', role: 'driver' });
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || (language === 'el' ? 'Αποτυχία δημιουργίας' : 'Failed to create user'));
        } finally {
            setAddingUser(false);
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            await userAPI.updateRole(userId, newRole);
            toast.success('Role updated successfully');
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update role');
        }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
        
        try {
            await userAPI.delete(userId);
            toast.success('User deleted successfully');
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete user');
        }
    };

    // Generate random password
    const generatePassword = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
        let password = '';
        for (let i = 0; i < 12; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return password;
    };

    // Open reset password modal
    const openResetModal = (user) => {
        setResetUser(user);
        const generated = generatePassword();
        setNewPassword(generated);
        setGeneratedPassword('');
        setShowPassword(false);
        setCopied(false);
        setShowResetModal(true);
    };

    // Handle password reset
    const handleResetPassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            toast.error(language === 'el' ? 'Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες' : 'Password must be at least 6 characters');
            return;
        }
        
        setResettingPassword(true);
        try {
            await api.post(`/users/${resetUser.id}/reset-password`, { new_password: newPassword });
            setGeneratedPassword(newPassword);
            toast.success(language === 'el' ? 'Ο κωδικός άλλαξε επιτυχώς!' : 'Password changed successfully!');
        } catch (error) {
            toast.error(error.response?.data?.detail || (language === 'el' ? 'Αποτυχία αλλαγής κωδικού' : 'Failed to reset password'));
        } finally {
            setResettingPassword(false);
        }
    };

    // Copy password to clipboard
    const copyToClipboard = () => {
        navigator.clipboard.writeText(generatedPassword || newPassword);
        setCopied(true);
        toast.success(language === 'el' ? 'Αντιγράφηκε!' : 'Copied!');
        setTimeout(() => setCopied(false), 2000);
    };

    // Open edit user modal
    const openEditModal = (user) => {
        setEditingUser(user);
        setEditUserData({
            name: user.name || '',
            phone: user.phone || '',
            role: user.role || 'driver'
        });
        setShowEditModal(true);
    };

    // Handle save user edits
    const handleSaveUser = async () => {
        if (!editUserData.name.trim()) {
            toast.error(language === 'el' ? 'Το όνομα είναι υποχρεωτικό' : 'Name is required');
            return;
        }
        
        // Phone required for fleet_manager
        if (editUserData.role === 'fleet_manager' && !editUserData.phone) {
            toast.error(language === 'el' ? 'Το τηλέφωνο είναι υποχρεωτικό για τον Υπεύθυνο Στόλου' : 'Phone is required for Fleet Manager');
            return;
        }
        
        setSavingUser(true);
        try {
            await api.put(`/users/${editingUser.id}`, editUserData);
            toast.success(language === 'el' ? 'Τα στοιχεία αποθηκεύτηκαν!' : 'User updated!');
            setShowEditModal(false);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || (language === 'el' ? 'Αποτυχία αποθήκευσης' : 'Failed to save'));
        } finally {
            setSavingUser(false);
        }
    };

    const filteredUsers = users.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="users-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">
                        {language === 'el' ? 'Διαχείριση Χρηστών' : 'User Management'}
                    </h1>
                    <p className="text-slate-500">
                        {users.length} {language === 'el' ? 'χρήστες' : 'total users'}
                        {maxUsers && ` / ${maxUsers} max`}
                    </p>
                </div>
                
                {/* Add User Button */}
                <Button
                    onClick={() => setShowAddModal(true)}
                    disabled={!canAddMore}
                    className="bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 text-white"
                    data-testid="add-user-btn"
                >
                    <UserPlus className="w-4 h-4 mr-2" />
                    {language === 'el' ? 'Προσθήκη Χρήστη' : 'Add User'}
                </Button>
            </div>
            
            {/* Personal Edition Info */}
            {isPersonalEdition && (
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                    <p className="text-violet-800 text-sm">
                        {language === 'el' 
                            ? `Οικογενειακό πρόγραμμα: ${users.length}/${maxUsers} χρήστες. Κάθε χρήστης μπορεί να συνδεθεί από το κινητό του.`
                            : `Family plan: ${users.length}/${maxUsers} users. Each user can login from their mobile device.`
                        }
                    </p>
                </div>
            )}

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-white border-slate-300 text-slate-900"
                    data-testid="users-search-input"
                />
            </div>

            {/* Table */}
            <div className="fleet-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Role</th>
                                <th>Joined</th>
                                <th className="w-12">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-8">
                                        <div className="empty-state">
                                            <Users className="empty-state-icon mx-auto" />
                                            <p>No users found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map((user) => (
                                    <tr key={user.id} className="table-row-hover" data-testid={`user-row-${user.id}`}>
                                        <td>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center text-[#1B7EB5] font-medium">
                                                    {user.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-slate-800">{user.name}</p>
                                                    {user.id === currentUser?.id && (
                                                        <span className="text-xs text-slate-500">(You)</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="text-slate-600">{user.email}</td>
                                        <td className="text-slate-600">{user.phone || '-'}</td>
                                        <td><RoleBadge role={user.role} /></td>
                                        <td className="text-slate-500">
                                            {new Date(user.created_at).toLocaleDateString()}
                                        </td>
                                        <td>
                                            {user.id !== currentUser?.id && (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-700">
                                                            <MoreVertical className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="bg-white border-slate-200">
                                                        <DropdownMenuItem 
                                                            onClick={() => openEditModal(user)}
                                                            className="cursor-pointer"
                                                        >
                                                            <Pencil className="w-4 h-4 mr-2 text-[#1B7EB5]" />
                                                            {language === 'el' ? 'Επεξεργασία' : 'Edit'}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator className="bg-slate-200" />
                                                        <DropdownMenuItem 
                                                            onClick={() => handleRoleChange(user.id, 'admin')}
                                                            className="cursor-pointer"
                                                            disabled={user.role === 'admin'}
                                                        >
                                                            <ShieldAlert className="w-4 h-4 mr-2 text-red-400" />
                                                            Make Admin
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem 
                                                            onClick={() => handleRoleChange(user.id, 'fleet_manager')}
                                                            className="cursor-pointer"
                                                            disabled={user.role === 'fleet_manager'}
                                                        >
                                                            <ShieldCheck className="w-4 h-4 mr-2 text-[#58A6B4]" />
                                                            Make Fleet Manager
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem 
                                                            onClick={() => handleRoleChange(user.id, 'driver')}
                                                            className="cursor-pointer"
                                                            disabled={user.role === 'driver'}
                                                        >
                                                            <Shield className="w-4 h-4 mr-2 text-green-400" />
                                                            Make Driver
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator className="bg-slate-200" />
                                                        <DropdownMenuItem 
                                                            onClick={() => openResetModal(user)}
                                                            className="cursor-pointer"
                                                        >
                                                            <Key className="w-4 h-4 mr-2 text-amber-500" />
                                                            {language === 'el' ? 'Επαναφορά Κωδικού' : 'Reset Password'}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator className="bg-slate-200" />
                                                        <DropdownMenuItem 
                                                            onClick={() => handleDelete(user.id)}
                                                            className="cursor-pointer text-red-400 focus:text-red-400"
                                                        >
                                                            <Trash2 className="w-4 h-4 mr-2" />
                                                            Delete User
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            
            {/* Add User Modal */}
            <Dialog open={showAddModal} onOpenChange={(open) => !addingUser && setShowAddModal(open)}>
                <DialogContent className="bg-white border-slate-200 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-[#1B7EB5]" />
                            {language === 'el' ? 'Προσθήκη Χρήστη' : 'Add User'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        {isPersonalEdition && (
                            <div className="bg-violet-50 rounded-lg p-3 text-sm text-violet-800">
                                {language === 'el' 
                                    ? 'Προσθέστε μέλη της οικογένειάς σας. Θα μπορούν να συνδέονται από το κινητό τους.'
                                    : 'Add family members. They will be able to login from their mobile devices.'
                                }
                            </div>
                        )}
                        
                        <div className="space-y-2">
                            <Label className="text-slate-700">{language === 'el' ? 'Όνομα' : 'Name'}</Label>
                            <Input
                                value={newUser.name}
                                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                                placeholder={language === 'el' ? 'π.χ. Μαρία' : 'e.g. Maria'}
                                className="bg-white border-slate-300"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="text-slate-700">Email</Label>
                            <Input
                                type="email"
                                value={newUser.email}
                                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                                placeholder="maria@example.com"
                                className="bg-white border-slate-300"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="text-slate-700">
                                {language === 'el' ? 'Τηλέφωνο' : 'Phone'}
                                {newUser.role === 'fleet_manager' && <span className="text-red-500 ml-1">*</span>}
                            </Label>
                            <Input
                                type="tel"
                                value={newUser.phone}
                                onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                                placeholder={language === 'el' ? 'π.χ. 6912345678' : 'e.g. 6912345678'}
                                className="bg-white border-slate-300"
                            />
                            {newUser.role === 'fleet_manager' && (
                                <p className="text-xs text-amber-600">
                                    {language === 'el' 
                                        ? 'Το τηλέφωνο θα εμφανίζεται στους οδηγούς ως "Υπεύθυνος Βάρδιας"' 
                                        : 'Phone will be shown to drivers as "Shift Supervisor"'}
                                </p>
                            )}
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="text-slate-700">{language === 'el' ? 'Κωδικός' : 'Password'}</Label>
                            <Input
                                type="password"
                                value={newUser.password}
                                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                                placeholder="••••••••"
                                className="bg-white border-slate-300"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="text-slate-700">{language === 'el' ? 'Ρόλος' : 'Role'}</Label>
                            <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                                <SelectTrigger className="bg-white border-slate-300">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                    <SelectItem value="driver">
                                        {language === 'el' ? 'Οδηγός' : 'Driver'}
                                    </SelectItem>
                                    <SelectItem value="fleet_manager">
                                        {language === 'el' ? 'Υπεύθυνος Στόλου' : 'Fleet Manager'}
                                    </SelectItem>
                                    <SelectItem value="admin">
                                        {language === 'el' ? 'Διαχειριστής' : 'Admin'}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="flex gap-3 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowAddModal(false)}
                                disabled={addingUser}
                                className="flex-1 border-slate-300"
                            >
                                {language === 'el' ? 'Ακύρωση' : 'Cancel'}
                            </Button>
                            <Button
                                onClick={handleAddUser}
                                disabled={addingUser}
                                className="flex-1 bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 text-white"
                            >
                                {addingUser ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                    <Plus className="w-4 h-4 mr-2" />
                                )}
                                {language === 'el' ? 'Δημιουργία' : 'Create'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Reset Password Modal */}
            <Dialog open={showResetModal} onOpenChange={(open) => !resettingPassword && setShowResetModal(open)}>
                <DialogContent className="bg-white border-slate-200 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <Key className="w-5 h-5 text-amber-500" />
                            {language === 'el' ? 'Επαναφορά Κωδικού' : 'Reset Password'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        {/* User Info */}
                        {resetUser && (
                            <div className="bg-slate-50 rounded-lg p-3">
                                <p className="text-sm text-slate-600">
                                    <span className="font-medium">{language === 'el' ? 'Χρήστης' : 'User'}:</span> {resetUser.name}
                                </p>
                                <p className="text-sm text-slate-600">
                                    <span className="font-medium">Email:</span> {resetUser.email}
                                </p>
                            </div>
                        )}
                        
                        {!generatedPassword ? (
                            <>
                                {/* New Password Input */}
                                <div className="space-y-2">
                                    <Label className="text-slate-700">
                                        {language === 'el' ? 'Νέος Κωδικός' : 'New Password'}
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            type={showPassword ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            className="bg-white border-slate-300 pr-20"
                                            placeholder={language === 'el' ? 'Εισάγετε νέο κωδικό' : 'Enter new password'}
                                        />
                                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-7 p-0"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </Button>
                                        </div>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setNewPassword(generatePassword())}
                                        className="text-xs"
                                    >
                                        {language === 'el' ? 'Δημιουργία Τυχαίου Κωδικού' : 'Generate Random Password'}
                                    </Button>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-3 pt-2">
                                    <Button
                                        variant="outline"
                                        onClick={() => setShowResetModal(false)}
                                        disabled={resettingPassword}
                                        className="flex-1 border-slate-300"
                                    >
                                        {language === 'el' ? 'Ακύρωση' : 'Cancel'}
                                    </Button>
                                    <Button
                                        onClick={handleResetPassword}
                                        disabled={resettingPassword || !newPassword}
                                        className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                                    >
                                        {resettingPassword ? (
                                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        ) : (
                                            <Key className="w-4 h-4 mr-2" />
                                        )}
                                        {language === 'el' ? 'Αλλαγή Κωδικού' : 'Reset Password'}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Success - Show Generated Password */}
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <Check className="w-6 h-6 text-green-600" />
                                    </div>
                                    <p className="text-green-800 font-medium mb-2">
                                        {language === 'el' ? 'Ο κωδικός άλλαξε επιτυχώς!' : 'Password changed successfully!'}
                                    </p>
                                    <p className="text-sm text-green-700 mb-3">
                                        {language === 'el' 
                                            ? 'Δώστε τον παρακάτω κωδικό στον χρήστη:' 
                                            : 'Give this password to the user:'}
                                    </p>
                                    <div className="flex items-center gap-2 bg-white border border-green-300 rounded-lg p-3">
                                        <code className="flex-1 text-lg font-mono text-slate-800">
                                            {generatedPassword}
                                        </code>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={copyToClipboard}
                                            className="h-8 w-8 p-0"
                                        >
                                            {copied ? (
                                                <Check className="w-4 h-4 text-green-600" />
                                            ) : (
                                                <Copy className="w-4 h-4" />
                                            )}
                                        </Button>
                                    </div>
                                </div>

                                <Button
                                    onClick={() => {
                                        setShowResetModal(false);
                                        setGeneratedPassword('');
                                    }}
                                    className="w-full bg-slate-600 hover:bg-slate-700 text-white"
                                >
                                    {language === 'el' ? 'Κλείσιμο' : 'Close'}
                                </Button>
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit User Modal */}
            <Dialog open={showEditModal} onOpenChange={(open) => !savingUser && setShowEditModal(open)}>
                <DialogContent className="bg-white border-slate-200 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <Pencil className="w-5 h-5 text-[#1B7EB5]" />
                            {language === 'el' ? 'Επεξεργασία Χρήστη' : 'Edit User'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        {/* Email (read-only) */}
                        {editingUser && (
                            <div className="bg-slate-50 rounded-lg p-3">
                                <p className="text-sm text-slate-500">Email</p>
                                <p className="text-slate-700 font-medium">{editingUser.email}</p>
                            </div>
                        )}
                        
                        {/* Name */}
                        <div className="space-y-2">
                            <Label className="text-slate-700">{language === 'el' ? 'Όνομα' : 'Name'}</Label>
                            <Input
                                value={editUserData.name}
                                onChange={(e) => setEditUserData({...editUserData, name: e.target.value})}
                                className="bg-white border-slate-300"
                            />
                        </div>
                        
                        {/* Phone */}
                        <div className="space-y-2">
                            <Label className="text-slate-700">
                                {language === 'el' ? 'Τηλέφωνο' : 'Phone'}
                                {editUserData.role === 'fleet_manager' && <span className="text-red-500 ml-1">*</span>}
                            </Label>
                            <Input
                                type="tel"
                                value={editUserData.phone}
                                onChange={(e) => setEditUserData({...editUserData, phone: e.target.value})}
                                placeholder={language === 'el' ? 'π.χ. 6912345678' : 'e.g. 6912345678'}
                                className="bg-white border-slate-300"
                            />
                            {editUserData.role === 'fleet_manager' && (
                                <p className="text-xs text-amber-600">
                                    {language === 'el' 
                                        ? 'Το τηλέφωνο θα εμφανίζεται στους οδηγούς ως "Υπεύθυνος Βάρδιας"' 
                                        : 'Phone will be shown to drivers as "Shift Supervisor"'}
                                </p>
                            )}
                        </div>
                        
                        {/* Role */}
                        <div className="space-y-2">
                            <Label className="text-slate-700">{language === 'el' ? 'Ρόλος' : 'Role'}</Label>
                            <Select 
                                value={editUserData.role} 
                                onValueChange={(v) => setEditUserData({...editUserData, role: v})}
                            >
                                <SelectTrigger className="bg-white border-slate-300">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                    <SelectItem value="driver">
                                        {language === 'el' ? 'Οδηγός' : 'Driver'}
                                    </SelectItem>
                                    <SelectItem value="fleet_manager">
                                        {language === 'el' ? 'Υπεύθυνος Στόλου' : 'Fleet Manager'}
                                    </SelectItem>
                                    <SelectItem value="admin">
                                        {language === 'el' ? 'Διαχειριστής' : 'Admin'}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowEditModal(false)}
                                disabled={savingUser}
                                className="flex-1 border-slate-300"
                            >
                                {language === 'el' ? 'Ακύρωση' : 'Cancel'}
                            </Button>
                            <Button
                                onClick={handleSaveUser}
                                disabled={savingUser}
                                className="flex-1 bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 text-white"
                            >
                                {savingUser ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                    <Check className="w-4 h-4 mr-2" />
                                )}
                                {language === 'el' ? 'Αποθήκευση' : 'Save'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
